import { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { findLesson } from "@/data/academy";
import { useAcademy } from "@/context/AcademyContext";
import { useSim } from "@/context/SimContext";
import { Card } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Target, Lightbulb, Cpu, Factory, Link2, PlayCircle,
  HelpCircle, CheckCircle2, Check, X, ChevronRight, GraduationCap,
} from "lucide-react";

const TAG_TONE = {
  MVP: "text-emerald-400 border-emerald-800 bg-emerald-950/30",
  FUTURE: "text-blue-400 border-blue-800 bg-blue-950/30",
  CONCEPT: "text-violet-400 border-violet-800 bg-violet-950/30",
};

export default function LessonView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isDone, completeLesson, submitQuiz } = useAcademy();
  const { trigger } = useSim();
  const { lesson, prev, next } = useMemo(() => findLesson(id), [id]);

  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  if (!lesson) {
    return (
      <div className="p-10 text-center text-slate-400">
        Lesson not found. <Link to="/academy" className="text-blue-400">Back to Academy</Link>
      </div>
    );
  }

  const done = isDone(lesson.id);
  const quiz = lesson.quiz || [];
  const score = quiz.reduce((acc, q, i) => acc + (answers[i] === q.answer ? 1 : 0), 0);

  const onSubmitQuiz = () => {
    if (Object.keys(answers).length < quiz.length) {
      toast.error("Please answer every question first.");
      return;
    }
    setSubmitted(true);
    submitQuiz(lesson.id, score, quiz.length);
    toast.success(`${t("yourScore")}: ${score}/${quiz.length}`);
  };

  const onComplete = () => {
    completeLesson(lesson.id);
    toast.success(t("completed"));
  };

  const onTryIt = async () => {
    const ti = lesson.tryIt;
    if (ti.scenario) {
      try { await trigger(ti.scenario); toast.success("Scenario triggered in Live Control"); } catch {}
    }
    navigate(ti.to);
  };

  return (
    <div className="space-y-5 ngx-fade-in max-w-4xl mx-auto" data-testid="lesson-view">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/academy")} data-testid="back-to-academy"
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-100">
          <ArrowLeft className="h-4 w-4" /> {t("backToAcademy")}
        </button>
        <span className={`text-[10px] font-mono px-2 py-1 rounded-sm border ${TAG_TONE[lesson.tag]}`} data-testid="lesson-tag">
          {t(`tag_${lesson.tag}`)}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-md bg-blue-600/15 border border-blue-800 flex items-center justify-center shrink-0">
          <GraduationCap className="h-5 w-5 text-blue-400" />
        </div>
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{lesson.pathTitle} · {t("level")} {lesson.level}</div>
          <h1 className="font-display text-2xl font-bold text-slate-50">{lesson.title}</h1>
        </div>
        {done && <CheckCircle2 className="h-6 w-6 text-emerald-400 ml-auto" data-testid="lesson-done-badge" />}
      </div>

      <Section icon={Target} title={t("learningObjective")} tone="text-blue-400">{lesson.objective}</Section>
      <Section icon={Lightbulb} title={t("simpleExplanation")} tone="text-amber-400">{lesson.simple}</Section>
      <Section icon={Cpu} title={t("technicalExplanation")} tone="text-cyan-400">{lesson.technical}</Section>

      {lesson.flow && (
        <Card className="p-5">
          <div className="flex flex-wrap items-center gap-2">
            {lesson.flow.map((step, i, arr) => (
              <span key={i} className="flex items-center gap-2">
                <span className="px-3 py-1.5 rounded-sm bg-[#050B14] border border-slate-700 text-slate-100 text-sm font-mono">{step}</span>
                {i < arr.length - 1 && <ChevronRight className="h-4 w-4 text-slate-600" />}
              </span>
            ))}
          </div>
        </Card>
      )}

      <Section icon={Factory} title={t("industrialExample")} tone="text-emerald-400">{lesson.example}</Section>
      <Section icon={Link2} title={t("howItRelates")} tone="text-violet-400">{lesson.ngx}</Section>

      {lesson.tryIt && (
        <Card className="p-5 border-blue-900/50 bg-blue-950/10 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <PlayCircle className="h-5 w-5 text-blue-400" />
            <span className="text-sm text-slate-200 font-medium">{t("tryIt")}</span>
          </div>
          <Button data-testid="lesson-tryit" onClick={onTryIt} className="h-10 bg-blue-600 hover:bg-blue-700 text-white">
            {lesson.tryIt.label} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </Card>
      )}

      {/* Knowledge check */}
      {quiz.length > 0 && (
        <Card className="p-5" data-testid="lesson-quiz">
          <div className="flex items-center gap-2 mb-4 text-slate-200">
            <HelpCircle className="h-4 w-4 text-blue-400" />
            <h3 className="font-display font-semibold">{t("knowledgeCheck")}</h3>
          </div>
          <div className="space-y-5">
            {quiz.map((q, qi) => (
              <div key={qi}>
                <div className="text-sm text-slate-200 font-medium mb-2">{qi + 1}. {q.q}</div>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    const chosen = answers[qi] === oi;
                    const isRight = submitted && oi === q.answer;
                    const isWrongChosen = submitted && chosen && oi !== q.answer;
                    return (
                      <button
                        key={oi} disabled={submitted}
                        data-testid={`quiz-${qi}-option-${oi}`}
                        onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                        className={`w-full text-left px-3 py-2.5 rounded-md border text-sm transition-colors ${
                          isRight ? "border-emerald-600 bg-emerald-950/30 text-emerald-200"
                          : isWrongChosen ? "border-red-600 bg-red-950/30 text-red-200"
                          : chosen ? "border-blue-500 bg-blue-950/20 text-slate-100"
                          : "border-slate-700 bg-[#050B14] text-slate-300 hover:border-slate-500"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          {isRight && <Check className="h-4 w-4" />}
                          {isWrongChosen && <X className="h-4 w-4" />}
                          {opt}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {submitted && (
                  <p className="text-xs text-slate-400 mt-2">
                    {answers[qi] === q.answer ? `✓ ${t("correct")}` : `✕ ${t("incorrect")}`} — {q.explain}
                  </p>
                )}
              </div>
            ))}
          </div>
          {!submitted ? (
            <Button data-testid="quiz-submit" onClick={onSubmitQuiz} className="mt-5 h-11 bg-blue-600 hover:bg-blue-700 text-white">
              {t("submitAnswers")}
            </Button>
          ) : (
            <div className="mt-5 flex items-center gap-3">
              <span className="font-mono text-lg text-slate-100">{t("yourScore")}: {score}/{quiz.length}</span>
              <Button data-testid="quiz-retake" onClick={() => { setSubmitted(false); setAnswers({}); }}
                className="h-9 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm">{t("retake")}</Button>
            </div>
          )}
        </Card>
      )}

      {/* Key takeaways */}
      {lesson.takeaways && (
        <Card className="p-5">
          <h3 className="font-display font-semibold text-slate-200 mb-3">{t("keyTakeaways")}</h3>
          <ul className="space-y-2">
            {lesson.takeaways.map((k, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" /> {k}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Footer nav */}
      <div className="flex items-center justify-between pt-2 pb-6">
        <Button disabled={!prev} onClick={() => prev && navigate(`/academy/lesson/${prev.id}`)} data-testid="lesson-prev"
          className="h-11 bg-[#0A1128] border border-slate-700 text-slate-200 hover:border-blue-500 disabled:opacity-40">
          <ArrowLeft className="h-4 w-4 mr-2" /> {t("previous")}
        </Button>
        {!done ? (
          <Button onClick={onComplete} data-testid="mark-complete" className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white">
            <Check className="h-4 w-4 mr-2" /> {t("markComplete")}
          </Button>
        ) : (
          <span className="flex items-center gap-2 text-emerald-400 text-sm font-medium"><CheckCircle2 className="h-4 w-4" /> {t("completed")}</span>
        )}
        <Button disabled={!next} onClick={() => next && navigate(`/academy/lesson/${next.id}`)} data-testid="lesson-next"
          className="h-11 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40">
          {t("next")} <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, tone, children }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${tone}`} />
        <h3 className="font-display font-semibold text-slate-200 text-sm uppercase tracking-wide">{title}</h3>
      </div>
      <p className="text-sm text-slate-300 leading-relaxed">{children}</p>
    </Card>
  );
}
